import { useMemo, useState } from 'react';
import {
  Box,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { debounce } from 'src/utils/timers';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';

interface StandaloneCustomPaginationProps {
  page: number;
  totalItems: number;
  pageSize: number;
  rowsPerPageOptions: number[];
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
}

const StandaloneCustomPagination = ({
  page,
  totalItems,
  pageSize,
  rowsPerPageOptions,
  onPageChange,
  onPageSizeChange,
}: StandaloneCustomPaginationProps): JSX.Element => {
  const [pageInput, setPageInput] = useState('');
  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    onPageSizeChange(Number(event.target.value));
    onPageChange(1);
    setPageInput('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingTop: 2,
        paddingLeft: 3,
        paddingRight: 3,
        paddingBottom: 2,
      }}
    >
      <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
        <Typography sx={{ typography: 'body2' }}>Show</Typography>
        <Select
          className="resultsPerPage"
          value={pageSize}
          onChange={handlePageSizeChange}
          sx={{
            marginLeft: 1,
            width: 30,
            height: 30,
            typography: 'body2',
            fontWeight: 'bold',
          }}
        >
          {rowsPerPageOptions.map((value) => (
            <MenuItem key={String(value)} value={value}>
              {value}
            </MenuItem>
          ))}
        </Select>
        <Typography sx={{ marginLeft: 1, typography: 'body2' }}>results per page</Typography>
      </Box>

      <Box>
        <Pagination
          shape="rounded"
          count={pageCount}
          page={page}
          onChange={(_, value) => {
            onPageChange(value);
            setPageInput('');
          }}
          showFirstButton
          showLastButton
        />
      </Box>

      <GoToPage
        pageCount={pageCount}
        pageInput={pageInput}
        onPageInput={setPageInput}
        onPageChange={onPageChange}
      />
    </Box>
  );
};

interface GoToPageProps {
  pageCount: number;
  pageInput: string;
  onPageInput: (val: string) => void;
  onPageChange: (newPage: number) => void;
}

const GoToPage = ({
  pageCount,
  pageInput,
  onPageInput,
  onPageChange,
}: GoToPageProps): JSX.Element => {
  const { i18n } = useLocales();

  const onPageChangeDebounced = useMemo(
    () =>
      debounce((newPage: number, input: string) => {
        if (newPage > pageCount || newPage < 1) return;
        onPageChange(newPage);
        onPageInput(input);
      }, 600),
    [onPageChange, onPageInput, pageCount]
  );

  const isError =
    !isNaN(parseInt(pageInput)) &&
    (Number(pageInput) > pageCount || Number(pageInput) < 1) &&
    pageCount !== 0;

  return (
    <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
      <Typography sx={{ typography: 'body2' }}>Go to Page</Typography>
      <Tooltip title={isError ? i18n('pagination.outOfRange') : ''}>
        <span>
          <TextField
            className="paginationInput"
            value={pageInput}
            autoComplete="off"
            error={isError}
            type="number"
            InputProps={{
              sx: {
                width: '30px',
                height: '30px',
                typography: 'body2',
                fontWeight: 'bold',
                padding: '0 !important',
                '& input': {
                  padding: '2px 1px !important',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  width: '100%',
                },
              },
            }}
            inputProps={{
              style: {
                textAlign: 'center',
                padding: '2px 1px',
              },
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const input = event.target.value;
              onPageInput(input);
              const newPage = Number(input);
              onPageChangeDebounced(newPage, input);
            }}
            sx={{
              marginLeft: 1,
              minWidth: '30px',
              width: '30px',
            }}
          />
        </span>
      </Tooltip>
      <Typography sx={{ marginLeft: 1, typography: 'body2' }}>
        of <span style={{ fontWeight: 'bold' }}>{pageCount}</span>{' '}
      </Typography>
    </Box>
  );
};

export default StandaloneCustomPagination;
